import { useState } from 'react';
import {
  Box,
  Chip,
  CircularProgress,
  makeStyles,
  Paper,
  Button,
  Tooltip,
  Typography,
} from '@material-ui/core';
import { useEntity } from '@backstage/plugin-catalog-react';
import { usePermission } from '@backstage/plugin-permission-react';
import { stringifyEntityRef } from '@backstage/catalog-model';
import type { Entity } from '@backstage/catalog-model';
import { toggleEnvironmentPermission } from 'shared/permissions';
import { usePipelineStatus, statusToColor, statusToLabel } from '../../hooks';
import { AdditionalParamsModal } from './additional-params-modal';

const useStyles = makeStyles(theme => ({
  paper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    padding: theme.spacing(2),
    cursor: 'pointer',
    transition: 'all 0.2s ease-in-out',
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
      transform: 'translateX(4px)',
    },
  },
  paperSelected: {
    backgroundColor: theme.palette.action.selected,
    borderLeft: `4px solid ${theme.palette.primary.main}`,
    transform: 'translateX(4px)',
    '&:hover': {
      backgroundColor: theme.palette.action.selected,
    },
  },
  box: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    flex: 1,
  },
  statusRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
}));

interface ToggleEnvironmentButtonProps {
  entity?: Entity;
  isSelected?: boolean;
  onSelect?: () => void;
}

export const ToggleEnvironmentButton = ({
  entity: propEntity,
  isSelected = false,
  onSelect,
}: ToggleEnvironmentButtonProps = {}) => {
  const classes = useStyles();
  const contextEntity = useEntity();
  const entity = propEntity || contextEntity.entity;

  const { allowed, loading: permLoading } = usePermission({
    permission: toggleEnvironmentPermission,
    resourceRef: stringifyEntityRef(entity),
  });

  const { status, isTriggering, triggerError, runUrl, handleTrigger } = usePipelineStatus(entity);

  const [modalOpen, setModalOpen] = useState(false);

  const additionalParams =
    (entity.metadata.environment as { workflow?: { additional_params?: string[] } } | undefined)
      ?.workflow?.additional_params ?? [];
  const hasAdditionalParams = additionalParams.length > 0;

  const handleTriggerClick = () => {
    if (hasAdditionalParams) {
      setModalOpen(true);
    } else {
      void handleTrigger();
    }
  };

  const handleModalConfirm = (params: string[]) => {
    void handleTrigger(params);
    setModalOpen(false);
  };

  const handleClick = (event: React.MouseEvent) => {
    if (onSelect && !(event.target as HTMLElement).closest('.MuiSwitch-root')) {
      onSelect();
    }
  };

  const label = statusToLabel(status);
  const color = statusToColor(status);
  const buttonDisabled = isTriggering || permLoading || !allowed;

  return (
    <>
      <Paper
        className={`${classes.paper} ${isSelected ? classes.paperSelected : ''}`}
        onClick={handleClick}
        elevation={isSelected ? 3 : 1}
      >
        <Box className={classes.box}>
          <Typography variant="h6">{entity.metadata.title || entity.metadata.name}</Typography>
          <Box className={classes.statusRow}>
            <Typography variant="body2" color="textSecondary">
              Status:
            </Typography>
            {isTriggering ? (
              <CircularProgress size={14} />
            ) : (
              <Tooltip title={runUrl ? `View run: ${runUrl}` : ''} placement="right">
                <Chip label={label} color={color} size="small" />
              </Tooltip>
            )}
          </Box>
          {triggerError && (
            <Typography variant="caption" color="error">
              {triggerError}
            </Typography>
          )}
        </Box>
        <Tooltip
          title={
            !allowed && !permLoading
              ? 'You do not have permission to trigger pipelines'
              : 'Trigger workflow'
          }
        >
          <span>
            <Button
              variant="contained"
              color="primary"
              size="small"
              disabled={buttonDisabled}
              onClick={e => {
                e.stopPropagation();
                handleTriggerClick();
              }}
            >
              Trigger
            </Button>
          </span>
        </Tooltip>
      </Paper>
      {hasAdditionalParams && (
        <AdditionalParamsModal
          open={modalOpen}
          additionalParams={additionalParams}
          onConfirm={handleModalConfirm}
          onClose={() => setModalOpen(false)}
        />
      )}
    </>
  );
};
